import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trash2, ChevronRight, ChevronDown } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";

interface Ingredient {
  item: string;
  quantity: number;
  unit: string;
}

interface Recipe {
  id: string;
  title: string;
  ingredients: Ingredient[];
  instructions: string[];
  created_at: string;
  profile_id: string;
  type: string;
}

const fetchSavedRecipes = async () => {
  const { data, error } = await supabase
    .from('saved_recipes')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

const deleteRecipe = async (id: string) => {
  const { error } = await supabase
    .from('saved_recipes')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

interface RecipeItemProps {
  recipe: Recipe;
  onDelete: (id: string) => Promise<void>;
}

const RecipeItem = ({ recipe, onDelete }: RecipeItemProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const formattedDate = new Date(recipe.created_at).toLocaleDateString();

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setIsDeleting(true);
      await onDelete(recipe.id);
    } catch (error) {
      console.error('Failed to delete recipe:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="rounded-lg border">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
            <div>
              <h4 className="font-medium">{recipe.title}</h4>
              <p className="text-sm text-gray-500">Added {formattedDate}</p>
              {recipe.type && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full mt-1 inline-block">
                  {recipe.type}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive/90"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t">
          <div className="space-y-4">
            <div>
              <h5 className="font-medium mb-2">Ingredients:</h5>
              <ul className="list-disc list-inside space-y-1">
                {recipe.ingredients.map((ingredient, index) => (
                  <li key={index} className="text-sm">
                    {ingredient.quantity} {ingredient.unit} {ingredient.item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="font-medium mb-2">Instructions:</h5>
              <ol className="list-decimal list-outside ml-4 space-y-2">
                {recipe.instructions.map((step, index) => (
                  <li key={index} className="text-sm">{step}</li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const SavedRecipes = ({ 
  open, 
  onOpenChange 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void 
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
    } else {
      const timer = setTimeout(() => {
        setMounted(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const queryClient = useQueryClient();

  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ['saved-recipes'],
    queryFn: fetchSavedRecipes,
    enabled: open,
    gcTime: 0,
    staleTime: 0
  });

  const handleDelete = async (id: string) => {
    await deleteRecipe(id);
    queryClient.invalidateQueries({ queryKey: ['saved-recipes'] });
  };

  if (!mounted) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={`w-full ${isMobile ? 'h-[100dvh] p-0' : 'max-w-md'}`}
      >
        <SheetHeader className="p-6 pb-2">
          <SheetTitle>Saved Recipes</SheetTitle>
        </SheetHeader>
        <div className="px-6 mt-4">
          {isLoading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <ScrollArea className="h-[calc(100vh-12rem)] w-full rounded-md">
              <div className="space-y-4">
                {recipes.map((recipe) => (
                  <RecipeItem 
                    key={recipe.id} 
                    recipe={recipe} 
                    onDelete={handleDelete}
                  />
                ))}
                {recipes.length === 0 && (
                  <div className="text-center py-8 px-4">
                    <p className="text-gray-500">
                      You have no saved recipes. Ask Kitty for recommendations, cook them, then if you like them, ask kitty to save them for you!
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};